import { type InputHTMLAttributes, type ReactNode, useId } from "react";

/**
 * Champ de saisie accessible : libellé + champ + aide/erreur, câblés ensemble
 * (`htmlFor`, `aria-describedby`, `aria-invalid`). Rend un `<input>` natif et
 * accepte ses attributs (`type`, `value`, `placeholder`, `required`…). Texte à
 * 16px (plancher de lecture, évite le zoom auto iOS) ; focus orange (§5.1).
 *
 * Quand l'utiliser : tout champ texte d'un formulaire. Le libellé est
 * obligatoire (a11y) ; passer `error` bascule le champ en état invalide et
 * masque l'aide au profit du message d'erreur (rôle `alert`).
 */
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Libellé visible, lié au champ (obligatoire — porte l'accessibilité). */
  label: ReactNode;
  /** Aide sous le champ. Masquée si une `error` est présente. */
  hint?: ReactNode;
  /** Message d'erreur : passe le champ en `aria-invalid` + filet rouge. */
  error?: ReactNode;
}

export function Input({ label, hint, error, className = "", id, ...rest }: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-small font-medium text-ink">
        {label}
      </label>
      <input
        id={inputId}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        className={[
          "w-full rounded-lg border border-line bg-surface px-3 py-2 text-body text-ink",
          "placeholder:text-muted",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:border-flambeau-500",
          error ? "border-red" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        {...rest}
      />
      {hint && !error && (
        <p id={hintId} className="text-small text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-small text-red-deep">
          {error}
        </p>
      )}
    </div>
  );
}
